import { useTranslation } from 'react-i18next';
import { CustomModal } from '../../CustomModal';
import { IconRadioSelector } from '../../IconRadioSelector';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    MDBBtn,
    MDBCheckbox,
    MDBIcon,
    MDBListGroup,
    MDBListGroupItem,
    MDBRadio,
} from 'mdb-react-ui-kit';
import * as fns from 'date-fns';
import API from '../../../service/api';
import { LoadingSpinner } from '../../LoadingSpinner';
import { DateRangePicker } from 'react-date-range';
import { useWindowSize } from '../../../hooks/useWindowSize';
import Lang from '../../../service/lang';
import { splitDatesIntoIntervals } from '../../../service/utils';
import { Alert } from '../../Alert';
import CashierReportModal, {
    CashierReportResponse,
} from '../CashierReportModal/CashierReportModal';
import {
    last12Months,
    last31Days,
    last7Days,
    lastMonth,
    lastWeek,
    ReportDateRange,
    thisMonth,
    thisWeek,
    today,
    yesterday,
} from './DateRanges';
import ProductsSoldReportModal, {
    ProductsReportResponse,
} from '../ProductsSoldReportModal/ProductsSoldReportModal';
import { SalesReportModal } from '../SalesReportModal';

export interface ReportSelectorModalProps {
    open: boolean;
    onClose: () => void;
    groupId: number;
    locationId?: number;
    machineId?: number;
}

export default function ReportSelectorModal({
    open,
    onClose,
    groupId,
    locationId,
    machineId,
}: ReportSelectorModalProps) {
    const { t } = useTranslation();
    const { innerWidth } = useWindowSize();

    const [reportType, setReportType] = useState<
        'sales' | 'products' | 'cashier'
    >('sales');
    const [selectedPeriodIdx, setSelectedPeriodIdx] = useState<
        number | string | null
    >(null);
    const [customDateRanges, setCustomDateRanges] = useState({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
    });
    const [productsSelectorType, setProductsSelectorType] = useState<
        string | null
    >(null);
    const [products, setProducts] = useState<any[] | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
    const [usersSelectorType, setUsersSelectorType] = useState<
        'me' | 'all' | 'custom'
    >('me');
    const [excludeUnknownUsers, setExcludeUnknownUsers] = useState(false);
    const [users, setUsers] = useState<any[] | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [salesReportData, setSalesReportData] = useState<any>(null);
    const [cashierReportData, setCashierReportData] =
        useState<CashierReportResponse | null>(null);
    const [productsReportData, setProductsReportData] =
        useState<ProductsReportResponse | null>(null);

    const mobile = useMemo(() => innerWidth < 800, [innerWidth]);
    const periods = useMemo<ReportDateRange[]>(
        () =>
            reportType == 'cashier'
                ? [today, yesterday, lastWeek, lastMonth]
                : [
                      today,
                      yesterday,
                      thisWeek,
                      thisMonth,
                      lastMonth,
                      last7Days,
                      last31Days,
                      last12Months,
                  ],
        [reportType],
    );
    const productSelectorTypes = useMemo(
        () => [
            { id: 'all_client', label: t('products_source_all_client') },
            { id: 'all_source', label: t('products_source_all_source') },
            { id: 'custom', label: t('products_source_custom') },
        ],
        [],
    );
    const usersSelectorTypes: { id: 'me' | 'all' | 'custom'; label: string }[] =
        [
            { id: 'me', label: 'me' },
            { id: 'all', label: 'all_users' },
            { id: 'custom', label: 'custom' },
        ];

    const inputIsValid = useMemo(
        () =>
            (typeof selectedPeriodIdx == 'number' ||
                selectedPeriodIdx == 'custom') &&
            (reportType == 'sales' ||
                reportType == 'cashier' ||
                (reportType == 'products' &&
                    (productsSelectorType == 'all_client' ||
                        productsSelectorType == 'all_source' ||
                        (productsSelectorType == 'custom' &&
                            selectedProducts.length > 0)))),
        [reportType, selectedPeriodIdx, productsSelectorType, selectedProducts],
    );

    const create = useCallback(async () => {
        setLoading(true);
        const { startDate, endDate } =
            selectedPeriodIdx == 'custom'
                ? customDateRanges
                : periods[Number(selectedPeriodIdx)]?.range();

        let baseURI = '/api/groups';
        if (groupId !== undefined) baseURI += `/${groupId}`;
        if (locationId !== undefined) baseURI += `/${locationId}`;
        if (machineId !== undefined) baseURI += `/${machineId}`;

        if (reportType == 'sales') {
            const uri = `${baseURI}/report`;
            const { resolution, intervals } = splitDatesIntoIntervals(
                startDate,
                endDate,
            );

            try {
                const data = await API.POST(uri, {
                    startDate,
                    endDate,
                    resolution,
                    intervals,
                });
                setSalesReportData(data);
            } catch (err: any) {
                setError(err.message);
                setLoading(false);
            }
        } else if (reportType == 'products') {
            const uri = `${baseURI}/report`;
            const { resolution, intervals } = splitDatesIntoIntervals(
                startDate,
                endDate,
            );
            const prodList = {
                all_client: 'all_client',
                all_source: 'all_source',
                custom: selectedProducts,
            }[productsSelectorType ?? ''];

            if (!prodList) return setError('Bad product list');

            try {
                const data = await API.POST(uri, {
                    startDate,
                    endDate,
                    resolution,
                    intervals,
                    prodList,
                });
                setProductsReportData(data);
            } catch (err: any) {
                setError(err.message);
                setLoading(false);
            }
        } else if (reportType == 'cashier') {
            const uri = `${baseURI}/cashier-report`;
            const usersList = {
                me: { type: 'me' },
                all: { type: 'all' },
                custom: { type: 'custom', ids: selectedUsers },
            }[usersSelectorType];
            const allowUnknownUsers = !excludeUnknownUsers;

            if (!usersList) return setError('Bad users list!');

            try {
                const data = await API.POST(uri, {
                    startDate,
                    endDate,
                    usersList,
                    allowUnknownUsers,
                });
                setCashierReportData(data);
            } catch (err: any) {
                setError(err.message);
                setLoading(false);
            }
        }
    }, [
        selectedPeriodIdx,
        customDateRanges,
        periods,
        reportType,
        selectedProducts,
        productsSelectorType,
        usersSelectorType,
        excludeUnknownUsers,
        groupId,
        locationId,
        machineId,
        onClose,
    ]);

    const Footer = useMemo(
        () => (
            <>
                {!loading && (
                    <>
                        <MDBBtn color="secondary" onClick={onClose}>
                            {t('cancel')}
                        </MDBBtn>
                        {!error && (
                            <MDBBtn
                                color="success"
                                disabled={!inputIsValid || loading}
                                onClick={create}
                            >
                                {t('create')}
                            </MDBBtn>
                        )}
                    </>
                )}
                {loading && (
                    <div className="d-flex flex-row align-items-center">
                        <LoadingSpinner className="me-1 p-0" size="sm" />
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {t('please_wait')}
                        </span>
                    </div>
                )}
            </>
        ),
        [create, onClose, inputIsValid, loading, error],
    );

    useEffect(() => {
        if (productsSelectorType == 'custom') {
            setSelectedProducts([]);
            API.GET(`/api/products`)
                .then(setProducts)
                .catch((err) => {
                    console.error(err);
                });
        }
    }, [productsSelectorType]);

    useEffect(() => {
        setSelectedPeriodIdx(null);
    }, [reportType]);

    useEffect(() => {
        if (usersSelectorType == 'custom') {
            setSelectedProducts([]);
            API.GET('/api/users/u')
                .then(setUsers)
                .catch((err) => {
                    console.error(err);
                });
        }
    }, [usersSelectorType]);

    useEffect(() => {
        if (!open) return;
        setReportType('sales');
        setSelectedPeriodIdx(null);
        setProductsSelectorType(null);
        setProducts(null);
        setSelectedProducts([]);
        setUsersSelectorType('me');
        setUsers(null);
        setSelectedUsers([]);
        setCustomDateRanges({
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        });
        setLoading(false);
        setError('');
    }, [open]);

    if (salesReportData)
        return (
            <SalesReportModal
                data={salesReportData}
                onClose={() => {
                    setSalesReportData(null);
                    onClose();
                }}
            />
        );

    if (cashierReportData)
        return (
            <CashierReportModal
                data={cashierReportData}
                onClose={() => {
                    setCashierReportData(null);
                    onClose();
                }}
            />
        );

    if (productsReportData)
        return (
            <ProductsSoldReportModal
                data={productsReportData}
                onClose={() => {
                    setProductsReportData(null);
                    onClose();
                }}
            />
        );

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={t('reports')}
            bodyClassName="d-flex flex-column align-items-start overflow-auto"
            bodyStyle={{ maxHeight: 400 }}
            footer={Footer}
        >
            <Alert type="danger" message={error} />

            {!error && (
                <>
                    <IconRadioSelector
                        items={[
                            {
                                id: 'sales',
                                name: t('event_type.sales'),
                                icon: 'fas fa-chart-line',
                            },
                            {
                                id: 'products',
                                name: t('products'),
                                icon: 'fas fa-shopping-cart',
                            },
                            {
                                id: 'cashier',
                                name: t('cashier'),
                                icon: 'fas fa-piggy-bank',
                            },
                        ]}
                        selected={reportType}
                        onSelect={(t) =>
                            setReportType(
                                String(t) as 'sales' | 'products' | 'cashier',
                            )
                        }
                        small={true}
                    />
                    <h6 className="mt-2">{t('time_period')}</h6>
                    <MDBListGroup>
                        {periods.map((p, idx) => (
                            <MDBListGroupItem
                                key={t(p.label_intl)}
                                className="border-0 p-0 d-flex flex-row align-items-center"
                            >
                                <MDBRadio
                                    name="period"
                                    value={idx}
                                    checked={selectedPeriodIdx == idx}
                                    onChange={() => setSelectedPeriodIdx(idx)}
                                    label={
                                        <div
                                            className={`d-flex flex-row align-items-center`}
                                            onClick={() =>
                                                setSelectedPeriodIdx(idx)
                                            }
                                        >
                                            <MDBIcon
                                                icon="calendar-days"
                                                color="primary"
                                                className="me-1"
                                                fixed
                                            />
                                            <span
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {t(p.label_intl)}
                                            </span>
                                        </div>
                                    }
                                />
                            </MDBListGroupItem>
                        ))}
                        <MDBListGroupItem className="border-0 p-0 d-flex flex-row align-items-center">
                            <MDBRadio
                                name="period"
                                value="custom"
                                checked={selectedPeriodIdx == 'custom'}
                                onChange={() => setSelectedPeriodIdx('custom')}
                                label={
                                    <div
                                        className={`d-flex flex-row align-items-center`}
                                        onClick={() =>
                                            setSelectedPeriodIdx('custom')
                                        }
                                    >
                                        <MDBIcon
                                            icon="pencil"
                                            color="primary"
                                            className="me-1"
                                            fixed
                                        />
                                        <span
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {t('custom')}
                                        </span>
                                    </div>
                                }
                            />
                        </MDBListGroupItem>
                    </MDBListGroup>

                    {selectedPeriodIdx == 'custom' && (
                        <>
                            <DateRangePicker
                                className={
                                    'rdr-no-inputs ' +
                                    (mobile ? 'rdr-mobile' : '')
                                }
                                ranges={[customDateRanges]}
                                onChange={(range: any) =>
                                    setCustomDateRanges(range['selection'])
                                }
                                maxDate={new Date()}
                                minDate={new Date(2023, 0, 1)}
                                staticRanges={[]}
                                inputRanges={[]}
                                locale={Lang.instance.getCurrentLocale()}
                            />
                        </>
                    )}

                    {reportType == 'products' && (
                        <>
                            <h6 className="mt-2">{t('products')}</h6>
                            <MDBListGroup>
                                {productSelectorTypes.map((ptype) => (
                                    <MDBListGroupItem
                                        key={ptype.id}
                                        className="border-0 p-0 d-flex flex-row align-items-center"
                                    >
                                        <MDBRadio
                                            name="ptype"
                                            value={ptype.id}
                                            checked={
                                                productsSelectorType == ptype.id
                                            }
                                            onChange={() =>
                                                setProductsSelectorType(
                                                    ptype.id,
                                                )
                                            }
                                            label={
                                                <div
                                                    className={`d-flex flex-row align-items-center`}
                                                    onClick={() =>
                                                        setProductsSelectorType(
                                                            ptype.id,
                                                        )
                                                    }
                                                >
                                                    <MDBIcon
                                                        icon="shopping-cart"
                                                        color="primary"
                                                        className="me-1"
                                                        fixed
                                                    />
                                                    <span
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow:
                                                                'ellipsis',
                                                        }}
                                                    >
                                                        {ptype.label}
                                                    </span>
                                                </div>
                                            }
                                        />
                                    </MDBListGroupItem>
                                ))}
                            </MDBListGroup>

                            {productsSelectorType == 'custom' && (
                                <>
                                    <div className="d-flex flex-row flex-wrap">
                                        {!products && (
                                            <LoadingSpinner size="sm" />
                                        )}
                                        {products && (
                                            <>
                                                {products.map((p) => (
                                                    <span
                                                        role="button"
                                                        className="badge badge-primary me-1 mb-1 d-flex flex-row"
                                                        style={{
                                                            userSelect: 'none',
                                                            opacity:
                                                                selectedProducts.includes(
                                                                    p.id,
                                                                )
                                                                    ? 1
                                                                    : 0.5,
                                                        }}
                                                        onClick={() =>
                                                            setSelectedProducts(
                                                                (prev) =>
                                                                    selectedProducts.includes(
                                                                        p.id,
                                                                    )
                                                                        ? prev.filter(
                                                                              (
                                                                                  pid,
                                                                              ) =>
                                                                                  pid !==
                                                                                  p.id,
                                                                          )
                                                                        : [
                                                                              ...prev,
                                                                              p.id,
                                                                          ],
                                                            )
                                                        }
                                                    >
                                                        <span
                                                            style={{
                                                                display:
                                                                    !selectedProducts.includes(
                                                                        p.id,
                                                                    )
                                                                        ? 'none'
                                                                        : 'block',
                                                            }}
                                                        >
                                                            <MDBIcon
                                                                icon="check"
                                                                color="secondary"
                                                                className="me-1"
                                                            />
                                                        </span>
                                                        {p.name}
                                                    </span>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {reportType == 'cashier' && (
                        <>
                            <h6 className="mt-2">{t('users')}</h6>
                            <MDBListGroup>
                                {usersSelectorTypes.map((utype) => (
                                    <MDBListGroupItem
                                        key={utype.id}
                                        className="border-0 p-0 d-flex flex-row align-items-center"
                                    >
                                        <MDBRadio
                                            name="utype"
                                            value={utype.id}
                                            checked={
                                                usersSelectorType == utype.id
                                            }
                                            onChange={() =>
                                                setUsersSelectorType(utype.id)
                                            }
                                            label={
                                                <div
                                                    className={`d-flex flex-row align-items-center`}
                                                    onClick={() =>
                                                        setUsersSelectorType(
                                                            utype.id,
                                                        )
                                                    }
                                                >
                                                    <MDBIcon
                                                        icon="user"
                                                        color="primary"
                                                        className="me-1"
                                                        fixed
                                                    />
                                                    <span
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow:
                                                                'ellipsis',
                                                        }}
                                                    >
                                                        {t(utype.label)}
                                                    </span>
                                                </div>
                                            }
                                        />
                                    </MDBListGroupItem>
                                ))}
                            </MDBListGroup>

                            {usersSelectorType == 'custom' && (
                                <>
                                    <div className="d-flex flex-row flex-wrap">
                                        {!users && <LoadingSpinner size="sm" />}
                                        {users && (
                                            <>
                                                {users.map((u) => (
                                                    <span
                                                        key={u.id}
                                                        role="button"
                                                        className="badge badge-primary me-1 mb-1 d-flex flex-row"
                                                        style={{
                                                            userSelect: 'none',
                                                            opacity:
                                                                selectedUsers.includes(
                                                                    u.id,
                                                                )
                                                                    ? 1
                                                                    : 0.5,
                                                        }}
                                                        onClick={() =>
                                                            setSelectedUsers(
                                                                (prev) =>
                                                                    selectedUsers.includes(
                                                                        u.id,
                                                                    )
                                                                        ? prev.filter(
                                                                              (
                                                                                  uid,
                                                                              ) =>
                                                                                  uid !==
                                                                                  u.id,
                                                                          )
                                                                        : [
                                                                              ...prev,
                                                                              u.id,
                                                                          ],
                                                            )
                                                        }
                                                    >
                                                        <span
                                                            style={{
                                                                display:
                                                                    !selectedUsers.includes(
                                                                        u.id,
                                                                    )
                                                                        ? 'none'
                                                                        : 'block',
                                                            }}
                                                        >
                                                            <MDBIcon
                                                                icon="check"
                                                                color="secondary"
                                                                className="me-1"
                                                            />
                                                        </span>
                                                        {u.Name}
                                                    </span>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                            <MDBCheckbox
                                type="checkbox"
                                label={t('exclude_unknown_users')}
                                id="excludeUnknown"
                                checked={excludeUnknownUsers}
                                onChange={() =>
                                    setExcludeUnknownUsers((prev) => !prev)
                                }
                            />
                        </>
                    )}
                </>
            )}
        </CustomModal>
    );
}
